'use client';

import { Checkbox, Flex, RadioButton } from '@instamenu/ui-system-next';
import { formatCurrency } from '@instamenu/formatters';
import { Box, Stack, Text, ItemProps } from '@instamenu/ui-system-next';
import { useSelectionGroup } from '@instamenu/ui-system-next';
import { useMenuItemContext } from '../menu-item-context';
import { ModifierFragment, ModifierGroupFragment } from '@instamenu/context-checkout';

const selectionInstructions = (group: ModifierGroupFragment) => {
  if (group.minSelect && group.maxSelect) {
    return group.minSelect === group.maxSelect
      ? `select ${group.minSelect}`
      : `select between ${group.minSelect} and ${group.maxSelect}`;
  } else if (group.minSelect && !group.maxSelect) {
    return `select at least ${group.minSelect}`;
  } else if (!group.minSelect && group.maxSelect) {
    return `select up to ${group.maxSelect}`;
  }
  return null;
};

const ModifierView = ({ component, data, ...props }: ItemProps<ModifierFragment>) => {
  const ctx = useMenuItemContext();
  const disabled = props.disabled || data.outOfStock;

  const renderPrice = () => {
    if (data.price == null || data.price <= 0) return null;
    if (ctx?.isPointRedemption) {
      return <Text>+{data.price.toLocaleString()} pts</Text>;
    }
    return <Text>{formatCurrency(data.price)}</Text>;
  };

  return (
    <Flex
      style={{
        display: 'flex',
        gap: 8,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
      as="label"
    >
      {component === 'RADIO' ? (
        <RadioButton {...props} disabled={disabled} />
      ) : (
        <Checkbox {...props} disabled={disabled} />
      )}
      <Box
        data-id="menu-modifier"
        css={{ flex: 1, textDecoration: data.outOfStock ? 'line-through' : undefined }}
      >
        {data.name}
      </Box>
      {renderPrice()}
    </Flex>
  );
};

export const ModifierGroupView = ({ data }: { data: ModifierGroupFragment }) => {
  const ctx = useMenuItemContext();

  const { options } = useSelectionGroup<ModifierFragment>({
    min: data.minSelect ?? undefined,
    max: data.maxSelect ?? undefined,
    data: data.modifiers,
    name: data.id,
    selected: ctx?.modifiersByGroupId(data.id) ?? [],
    onChange: (items) => ctx?.setModifiersForGroup(data.id, items),
    itemEquals: (item, datum) => item.id === datum.id,
  });

  return (
    <Box css={{ marginTop: 8 }}>
      <Text variant="subtitle1">{data.name}</Text>
      {(data.minSelect || data.maxSelect) && (
        <Text
          variant="caption1"
          error={!!ctx?.modifierGroupErrors.find((id) => id === data.id)}
          css={{ marginTop: 4 }}
        >
          {selectionInstructions(data)}
        </Text>
      )}
      <Stack style={{ paddingTop: 8, paddingBottom: 16, gap: 8 }}>
        {options.map(({ props }) => (
          <ModifierView key={props.data.id} {...props} />
        ))}
      </Stack>
    </Box>
  );
};
